// Version: 1.0.6 - Scroll Button Fix
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import logoImg from '../logo.jpg';

export const LandingPage: React.FC = () => {
    const navigate = useNavigate();
    const [shopDomain, setShopDomain] = useState('');
    const [error, setError] = useState('');
    const [activeTab, setActiveTab] = useState('overview');
    const [openFaq, setOpenFaq] = useState<number | null>(null);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [showScrollTop, setShowScrollTop] = useState(false);
    const pageRef = useRef<HTMLDivElement>(null);
    console.log('LandingPage version 1.0.7', showScrollTop);

    useEffect(() => {
        // rAF polling - reads every possible scroll container every frame
        // This is 100% reliable regardless of which element is scrolling
        let rafId: number;
        const checkScroll = () => {
            const winScroll = window.scrollY || window.pageYOffset || 0;
            const htmlScroll = document.documentElement.scrollTop || 0;
            const bodyScroll = document.body.scrollTop || 0;
            const pageScroll = pageRef.current?.scrollTop || 0;
            const rootScroll = document.getElementById('root')?.scrollTop || 0;
            const maxScroll = Math.max(winScroll, htmlScroll, bodyScroll, pageScroll, rootScroll);
            setShowScrollTop(maxScroll > 300);
            rafId = requestAnimationFrame(checkScroll);
        };
        rafId = requestAnimationFrame(checkScroll);
        return () => cancelAnimationFrame(rafId);
    }, []);

    const scrollToTop = () => {
        // Scroll the ref container
        if (pageRef.current) {
            pageRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
        try { window.scrollTo({ top: 0, behavior: 'smooth' }); } catch (err) {}
        try { document.documentElement.scrollTo({ top: 0, behavior: 'smooth' }); } catch (err) {}
        try { document.body.scrollTo({ top: 0, behavior: 'smooth' }); } catch (err) {}
        const root = document.getElementById('root');
        if (root) {
            try { root.scrollTo({ top: 0, behavior: 'smooth' }); } catch (err) {}
        }
    };

    const handleScroll = (e: React.MouseEvent<HTMLElement>, targetId: string) => {
        e.preventDefault();
        const element = document.getElementById(targetId);
        if (element) {
            element.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
            });
        }
    };

    const handleInstall = (e: React.FormEvent) => {
        e.preventDefault();
        setError('');

        if (!shopDomain.trim()) {
            setError('Please enter your Shopify store URL');
            return;
        }

        let cleanDomain = shopDomain.trim().toLowerCase();
        cleanDomain = cleanDomain.replace(/^(https?:\/\/)?(www\.)?/, '');
        cleanDomain = cleanDomain.replace(/\/$/, '');

        if (!cleanDomain.includes('.') && !cleanDomain.includes('myshopify.com')) {
            cleanDomain = `${cleanDomain}.myshopify.com`;
        }

        window.location.href = `/api/auth/login?shop=${encodeURIComponent(cleanDomain)}`;
    };

    // FAQ list
    const faqs = [
        {
            q: "How does real-time sync work?",
            a: "Tvora uses Shopify Webhooks combined with high-speed queue workers. The moment a customer purchases, returns, or cancels an order on your Shopify store, Tvora receives and processes the payload within 2.5 seconds to update your dashboards instantly."
        },
        {
            q: "Does Tvora support Shopify Plus features?",
            a: "Yes, Tvora is fully compatible with Shopify Plus stores. We support multi-currency checkouts, advanced shipping rules, custom checkout parameters, and large order volume synchronization without throttling."
        },
        {
            q: "Can I export data and reports?",
            a: "Absolutely. You can export complete CSV/Excel formats of orders, product intelligence, and customer segmentation lists with one click. Automated daily or weekly executive report exports can also be scheduled."
        },
        {
            q: "How does Tvora calculate net profit margins?",
            a: "Tvora pulls order values directly from Shopify and deducts costs configured in your Settings dashboard: Cost of Goods Sold (COGS) for each product variant, shipping costs, handling fees, transaction gateways, and optional marketing ad spend."
        },
        {
            q: "Can we build custom customer segments?",
            a: "Yes. Tvora's advanced segmentation engine allows multi-conditional rules. You can filter customers based on purchase behavior, product tags, order dates, return history, LTV levels, and geolocations, and export them for targeted marketing."
        },
        {
            q: "Is my store's data safe with Tvora?",
            a: "Security is our highest priority. We use secure Shopify OAuth 2.0 authentication, database level encryption at rest, and TLS 1.3 transmission protocols. We never share, sell, or rent your store data with any external entities."
        }
    ];

    // Tabs configuration
    const tabs = [
        { id: 'overview', name: 'Overview Dashboard' },
        { id: 'orders', name: 'Order Intelligence' },
        { id: 'customers', name: 'Customer Hub' },
        { id: 'products', name: 'Product Intelligence' },
        { id: 'segments', name: 'Segmentation Engine' },
        { id: 'intel', name: 'Intelligence Center' },
        { id: 'settings', name: 'Settings' }
    ];

    return (
        <>
        <div ref={pageRef} style={{
            minHeight: '100vh',
            fontFamily: "'Outfit', 'Inter', system-ui, -apple-system, sans-serif",
            color: '#0f172a',
            background: 'linear-gradient(135deg, #f8fafc 0%, #eff6ff 40%, #f5f3ff 70%, #faf5ff 100%)',
            overflowX: 'hidden',
            scrollBehavior: 'smooth'
        }}>
            {/* Scroll sentinel - when this leaves viewport, show scroll-to-top button */}
            <div id="scroll-sentinel" style={{ position: 'absolute', top: '400px', height: '1px', width: '1px', pointerEvents: 'none' }} />
            {/* Nav Header */}
            {/* Nav Header */}
            <header className="landing-header" style={{
                position: 'sticky',
                top: 0,
                zIndex: 1000,
                background: 'rgba(255, 255, 255, 0.75)',
                backdropFilter: 'blur(20px)',
                WebkitBackdropFilter: 'blur(20px)',
                borderBottom: '1px solid rgba(241, 245, 249, 0.8)',
                padding: '1.25rem 2rem',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                boxShadow: '0 4px 30px rgba(0, 0, 0, 0.01)'
            }}>
                <div 
                    className="header-logo" 
                    onClick={scrollToTop}
                    style={{ display: 'flex', alignItems: 'center', gap: '0.85rem', cursor: 'pointer' }}
                >
                    <img 
                        src={logoImg} 
                        alt="Tvora Logo" 
                        style={{
                            width: '38px',
                            height: '38px',
                            borderRadius: '10px',
                            objectFit: 'cover',
                            boxShadow: '0 4px 20px rgba(99, 102, 241, 0.15)'
                        }} 
                    />
                    <div>
                        <span className="logo-title" style={{
                            fontWeight: 800,
                            fontSize: '1.45rem',
                            letterSpacing: '-0.03em',
                            background: 'linear-gradient(135deg, #1e293b 0%, #6366f1 100%)',
                            WebkitBackgroundClip: 'text',
                            backgroundClip: 'text',
                            WebkitTextFillColor: 'transparent'
                        }}>
                            Tvora
                        </span>
                        <span className="logo-subtitle" style={{
                            display: 'block',
                            fontSize: '0.65rem',
                            color: '#64748b',
                            fontWeight: 600,
                            letterSpacing: '0.08em',
                            textTransform: 'uppercase',
                            marginTop: '-2px'
                        }}>
                            Analytics & Profit
                        </span>
                    </div>
                </div>

                <nav style={{ display: 'flex', gap: '2rem', alignItems: 'center' }} className="nav-desktop">
                    <a href="#features" onClick={(e) => handleScroll(e, 'features')} style={{ color: '#475569', textDecoration: 'none', fontWeight: 500, fontSize: '0.95rem', transition: 'color 0.2s' }}>Features</a>
                    <a href="#showcase" onClick={(e) => handleScroll(e, 'showcase')} style={{ color: '#475569', textDecoration: 'none', fontWeight: 500, fontSize: '0.95rem', transition: 'color 0.2s' }}>Interactive Preview</a>
                    <a href="#who-is-it-for" onClick={(e) => handleScroll(e, 'who-is-it-for')} style={{ color: '#475569', textDecoration: 'none', fontWeight: 500, fontSize: '0.95rem', transition: 'color 0.2s' }}>Target Audience</a>

                    <a href="#faq" onClick={(e) => handleScroll(e, 'faq')} style={{ color: '#475569', textDecoration: 'none', fontWeight: 500, fontSize: '0.95rem', transition: 'color 0.2s' }}>FAQ</a>
                </nav>

                <div className="header-actions" style={{ display: 'flex', gap: '0.85rem', alignItems: 'center' }}>
                    <a href="#install" onClick={(e) => handleScroll(e, 'install')} style={{
                        background: 'rgba(255, 255, 255, 0.9)',
                        border: '1px solid rgba(226, 232, 240, 0.8)',
                        color: '#0f172a',
                        padding: '0.6rem 1.2rem',
                        borderRadius: '12px',
                        textDecoration: 'none',
                        fontWeight: 600,
                        fontSize: '0.9rem',
                        transition: 'all 0.2s'
                    }}>
                        Log In
                    </a>
                    <a href="#install" onClick={(e) => handleScroll(e, 'install')} style={{
                        background: 'linear-gradient(135deg, #6366f1 0%, #3b82f6 100%)',
                        color: 'white',
                        padding: '0.65rem 1.35rem',
                        borderRadius: '12px',
                        textDecoration: 'none',
                        fontWeight: 600,
                        fontSize: '0.9rem',
                        boxShadow: '0 8px 25px rgba(99, 102, 241, 0.25)',
                        transition: 'all 0.2s'
                    }}>
                        Start Free Trial
                    </a>
                </div>

                {/* Hamburger Toggle Button */}
                <button
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    className="hamburger-button"
                    style={{
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '0.5rem',
                        color: '#0f172a',
                        alignItems: 'center',
                        justifyContent: 'center'
                    }}
                >
                    {isMobileMenuOpen ? (
                        <svg style={{ width: '26px', height: '26px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    ) : (
                        <svg style={{ width: '26px', height: '26px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                        </svg>
                    )}
                </button>
            </header>

            {/* Mobile Navigation Drawer */}
            <AnimatePresence>
                {isMobileMenuOpen && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        transition={{ duration: 0.25 }}
                        style={{
                            background: 'rgba(255, 255, 255, 0.98)',
                            backdropFilter: 'blur(20px)',
                            WebkitBackdropFilter: 'blur(20px)',
                            borderBottom: '1px solid rgba(226, 232, 240, 0.8)',
                            position: 'fixed',
                            top: '64px',
                            left: 0,
                            right: 0,
                            zIndex: 999,
                            overflow: 'hidden',
                            boxShadow: '0 15px 30px rgba(0, 0, 0, 0.05)'
                        }}
                        className="mobile-menu-drawer"
                    >
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            padding: '1.5rem',
                            gap: '1.25rem'
                        }}>
                            <a href="#features" onClick={(e) => { setIsMobileMenuOpen(false); handleScroll(e, 'features'); }} style={{ color: '#0f172a', textDecoration: 'none', fontWeight: 700, fontSize: '1.05rem', padding: '0.25rem 0' }}>Features</a>
                            <a href="#showcase" onClick={(e) => { setIsMobileMenuOpen(false); handleScroll(e, 'showcase'); }} style={{ color: '#0f172a', textDecoration: 'none', fontWeight: 700, fontSize: '1.05rem', padding: '0.25rem 0' }}>Interactive Preview</a>
                            <a href="#who-is-it-for" onClick={(e) => { setIsMobileMenuOpen(false); handleScroll(e, 'who-is-it-for'); }} style={{ color: '#0f172a', textDecoration: 'none', fontWeight: 700, fontSize: '1.05rem', padding: '0.25rem 0' }}>Target Audience</a>

                            <a href="#faq" onClick={(e) => { setIsMobileMenuOpen(false); handleScroll(e, 'faq'); }} style={{ color: '#0f172a', textDecoration: 'none', fontWeight: 700, fontSize: '1.05rem', padding: '0.25rem 0' }}>FAQ</a>
                            <hr style={{ border: 'none', borderTop: '1px solid #f1f5f9', margin: '0.25rem 0' }} />
                            <a href="#install" onClick={(e) => { setIsMobileMenuOpen(false); handleScroll(e, 'install'); }} style={{
                                background: '#f1f5f9',
                                color: '#0f172a',
                                padding: '0.75rem',
                                borderRadius: '12px',
                                textDecoration: 'none',
                                fontWeight: 700,
                                textAlign: 'center',
                                fontSize: '0.95rem'
                            }}>
                                Log In
                            </a>
                            <a href="#install" onClick={(e) => { setIsMobileMenuOpen(false); handleScroll(e, 'install'); }} style={{
                                background: 'linear-gradient(135deg, #6366f1 0%, #3b82f6 100%)',
                                color: 'white',
                                padding: '0.75rem',
                                borderRadius: '12px',
                                textDecoration: 'none',
                                fontWeight: 700,
                                textAlign: 'center',
                                fontSize: '0.95rem',
                                boxShadow: '0 4px 15px rgba(99, 102, 241, 0.2)'
                            }}>
                                Start Free Trial
                            </a>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Hero Section */}
            <section style={{
                position: 'relative',
                padding: '5.5rem 2rem 5rem 2rem',
                maxWidth: '1280px',
                margin: '0 auto',
                width: '100%',
                boxSizing: 'border-box'
            }}>
                {/* Hero Gradient Blur */}
                <div style={{
                    position: 'absolute',
                    top: '-10%',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    width: '600px',
                    height: '600px',
                    background: 'radial-gradient(circle, rgba(99,102,241,0.08) 0%, rgba(59,130,246,0.05) 50%, rgba(255,255,255,0) 100%)',
                    zIndex: -1,
                    filter: 'blur(60px)'
                }} />

                <div style={{
                    display: 'grid',
                    gridTemplateColumns: '1.1fr 0.9fr',
                    gap: '4.5rem',
                    alignItems: 'center'
                }} className="hero-grid">
                    <motion.div
                        initial={{ opacity: 0, y: 25 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        <div style={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '0.6rem',
                            background: 'rgba(255, 255, 255, 0.8)',
                            border: '1px solid rgba(99, 102, 241, 0.15)',
                            padding: '0.45rem 1rem',
                            borderRadius: '9999px',
                            marginBottom: '1.75rem',
                            boxShadow: '0 4px 15px rgba(99, 102, 241, 0.04)'
                        }}>
                            <span style={{
                                width: '6px',
                                height: '6px',
                                borderRadius: '50%',
                                background: '#10b981',
                                display: 'inline-block'
                            }} />
                            <span style={{ fontSize: '0.78rem', fontWeight: 600, color: '#4f46e5', letterSpacing: '0.02em' }}>
                                Shopify Business Intelligence Platform
                            </span>
                        </div>

                        <h1 style={{
                            fontSize: '3.6rem',
                            fontWeight: 900,
                            lineHeight: 1.12,
                            letterSpacing: '-0.035em',
                            color: '#0f172a',
                            margin: '0 0 1.5rem 0'
                        }}>
                            Run Your Shopify Store With <span style={{
                                background: 'linear-gradient(135deg, #6366f1 0%, #3b82f6 100%)',
                                WebkitBackgroundClip: 'text',
                                backgroundClip: 'text',
                                WebkitTextFillColor: 'transparent'
                            }}>Complete Business Intelligence</span>
                        </h1>

                        <p style={{
                            fontSize: '1.2rem',
                            color: '#475569',
                            lineHeight: 1.65,
                            margin: '0 0 2.5rem 0',
                            maxWidth: '560px'
                        }}>
                            Track profits, monitor inventory, analyze customers, reduce RTO losses, and scale smarter — all from one unified dashboard.
                        </p>

                        <div style={{ display: 'flex', gap: '1rem', marginBottom: '2.5rem' }}>
                            <a href="#install" onClick={(e) => handleScroll(e, 'install')} style={{
                                background: 'linear-gradient(135deg, #6366f1 0%, #3b82f6 100%)',
                                color: 'white',
                                padding: '0.85rem 1.85rem',
                                borderRadius: '14px',
                                textDecoration: 'none',
                                fontWeight: 600,
                                fontSize: '0.98rem',
                                boxShadow: '0 10px 25px rgba(99, 102, 241, 0.28)',
                                transition: 'all 0.2s'
                            }}>
                                Start Free Trial
                            </a>
                            <a href="#showcase" onClick={(e) => handleScroll(e, 'showcase')} style={{
                                background: 'rgba(255, 255, 255, 0.8)',
                                border: '1px solid rgba(226, 232, 240, 0.8)',
                                color: '#334155',
                                padding: '0.85rem 1.85rem',
                                borderRadius: '14px',
                                textDecoration: 'none',
                                fontWeight: 600,
                                fontSize: '0.98rem',
                                transition: 'all 0.2s'
                            }}>
                                Book Demo
                            </a>
                        </div>

                        {/* Interactive Installation box */}
                        <div id="install" style={{
                            background: 'rgba(255, 255, 255, 0.75)',
                            backdropFilter: 'blur(20px)',
                            WebkitBackdropFilter: 'blur(20px)',
                            border: '1px solid rgba(226, 232, 240, 0.8)',
                            padding: '1.5rem',
                            borderRadius: '20px',
                            maxWidth: '500px',
                            boxShadow: '0 20px 40px rgba(15, 23, 42, 0.04)'
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.85rem' }}>
                                <svg style={{ width: '20px', height: '20px', color: '#10b981' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                                </svg>
                                <span style={{ fontSize: '0.9rem', fontWeight: 600, color: '#334155' }}>Install on your Shopify Store</span>
                            </div>
                            <form onSubmit={handleInstall} style={{ display: 'flex', gap: '0.65rem' }}>
                                <input
                                    type="text"
                                    placeholder="your-store-name.myshopify.com"
                                    value={shopDomain}
                                    onChange={(e) => {
                                        setShopDomain(e.target.value);
                                        if (error) setError('');
                                    }}
                                    style={{
                                        flex: 1,
                                        padding: '0.8rem 1rem',
                                        borderRadius: '12px',
                                        border: '1.5px solid rgba(226, 232, 240, 0.9)',
                                        background: 'white',
                                        outline: 'none',
                                        fontSize: '0.92rem',
                                        color: '#0f172a',
                                        boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.01)',
                                        transition: 'all 0.2s'
                                    }}
                                />
                                <button type="submit" style={{
                                    background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
                                    color: 'white',
                                    border: 'none',
                                    padding: '0 1.5rem',
                                    borderRadius: '12px',
                                    fontWeight: 600,
                                    fontSize: '0.92rem',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    boxShadow: '0 4px 12px rgba(15, 23, 42, 0.15)'
                                }}>
                                    Install Now
                                </button>
                            </form>
                            {error && (
                                <p style={{ color: '#ef4444', fontSize: '0.8rem', margin: '0.6rem 0 0 0', fontWeight: 500, display: 'flex', alignItems: 'center', gap: '0.25rem' }}>
                                    <span>⚠️</span> {error}
                                </p>
                            )}
                        </div>
                    </motion.div>

                    {/* Floating Dashboard Visuals */}
                    <motion.div
                        initial={{ opacity: 0, scale: 0.96 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.8, delay: 0.25 }}
                        style={{ position: 'relative', height: '480px' }}
                        className="hero-graphics"
                    >
                        {/* Main floating widget */}
                        <div style={{
                            position: 'absolute',
                            top: '10%',
                            right: '5%',
                            width: '100%',
                            maxWidth: '380px',
                            background: 'rgba(255, 255, 255, 0.85)',
                            backdropFilter: 'blur(24px)',
                            WebkitBackdropFilter: 'blur(24px)',
                            borderRadius: '24px',
                            border: '1px solid rgba(255, 255, 255, 0.7)',
                            boxShadow: '0 25px 60px rgba(15, 23, 42, 0.08)',
                            padding: '1.5rem',
                            zIndex: 2
                        }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.25rem' }}>
                                <div>
                                    <h4 style={{ margin: 0, fontSize: '0.9rem', color: '#64748b', fontWeight: 600 }}>Total Store Net Profit</h4>
                                    <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.5rem', marginTop: '0.2rem' }}>
                                        <span style={{ fontSize: '1.8rem', fontWeight: 900, color: '#0f172a' }}>$38,492.20</span>
                                        <span style={{ fontSize: '0.78rem', color: '#10b981', fontWeight: 600, background: 'rgba(16, 185, 129, 0.1)', padding: '0.15rem 0.45rem', borderRadius: '6px' }}>+18.4%</span>
                                    </div>
                                </div>
                                <div style={{ width: '42px', height: '42px', borderRadius: '12px', background: 'linear-gradient(135deg, #d8b4fe 0%, #818cf8 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white' }}>
                                    <svg style={{ width: '22px', height: '22px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                    </svg>
                                </div>
                            </div>
                            {/* Graphic Chart */}
                            <svg viewBox="0 0 300 80" style={{ width: '100%', height: 'auto', overflow: 'visible' }}>
                                <defs>
                                    <linearGradient id="profitGrad" x1="0" y1="0" x2="0" y2="1">
                                        <stop offset="0%" stopColor="#6366f1" stopOpacity="0.25" />
                                        <stop offset="100%" stopColor="#6366f1" stopOpacity="0.0" />
                                    </linearGradient>
                                </defs>
                                <path d="M 0 60 Q 50 30 100 45 T 200 15 T 300 5 L 300 80 L 0 80 Z" fill="url(#profitGrad)" />
                                <path d="M 0 60 Q 50 30 100 45 T 200 15 T 300 5" fill="none" stroke="#6366f1" strokeWidth="3" strokeLinecap="round" />
                                <circle cx="200" cy="15" r="4.5" fill="#3b82f6" stroke="white" strokeWidth="2" />
                            </svg>
                        </div>

                        {/* Smaller stats widget (RTO Risk) */}
                        <div style={{
                            position: 'absolute',
                            bottom: '12%',
                            left: '2%',
                            width: '100%',
                            maxWidth: '240px',
                            background: 'rgba(255, 255, 255, 0.9)',
                            backdropFilter: 'blur(20px)',
                            WebkitBackdropFilter: 'blur(20px)',
                            borderRadius: '20px',
                            border: '1px solid rgba(255, 255, 255, 0.7)',
                            boxShadow: '0 20px 45px rgba(15, 23, 42, 0.06)',
                            padding: '1.2rem',
                            zIndex: 3
                        }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.85rem' }}>
                                <div style={{ width: '32px', height: '32px', borderRadius: '10px', background: 'rgba(239, 68, 68, 0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#ef4444' }}>
                                    <svg style={{ width: '18px', height: '18px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                                    </svg>
                                </div>
                                <div>
                                    <span style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 600 }}>RTO Prevention</span>
                                    <h5 style={{ margin: 0, fontSize: '0.88rem', fontWeight: 800, color: '#0f172a' }}>Risk Alert: High</h5>
                                </div>
                            </div>
                            <div style={{ fontSize: '0.75rem', color: '#475569', lineHeight: 1.4 }}>
                                <strong>3 order cancel actions</strong> triggered automatically based on risk indicators.
                            </div>
                        </div>

                        {/* Customer Retention metrics floating card */}
                        <div style={{
                            position: 'absolute',
                            top: '38%',
                            left: '-8%',
                            width: '100%',
                            maxWidth: '220px',
                            background: 'rgba(255, 255, 255, 0.85)',
                            backdropFilter: 'blur(20px)',
                            WebkitBackdropFilter: 'blur(20px)',
                            borderRadius: '20px',
                            border: '1px solid rgba(255, 255, 255, 0.7)',
                            boxShadow: '0 20px 45px rgba(15, 23, 42, 0.05)',
                            padding: '1.1rem',
                            zIndex: 4
                        }}>
                            <span style={{ fontSize: '0.7rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Customer Retention</span>
                            <div style={{ fontSize: '1.4rem', fontWeight: 900, color: '#0f172a', margin: '0.2rem 0' }}>82.4% LTV</div>
                            <div style={{ display: 'flex', gap: '0.3rem', alignItems: 'center', marginTop: '0.4rem' }}>
                                <div style={{ height: '6px', flex: 1, background: '#e2e8f0', borderRadius: '3px', overflow: 'hidden' }}>
                                    <div style={{ height: '100%', width: '82%', background: 'linear-gradient(to right, #6366f1, #3b82f6)', borderRadius: '3px' }} />
                                </div>
                                <span style={{ fontSize: '0.68rem', color: '#475569', fontWeight: 700 }}>VIP tier</span>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>

            <section style={{
                background: 'rgba(255, 255, 255, 0.4)',
                borderTop: '1px solid rgba(241, 245, 249, 0.9)',
                borderBottom: '1px solid rgba(241, 245, 249, 0.9)',
                padding: '3rem 2rem'
            }}>
                <div style={{ maxWidth: '1200px', margin: '0 auto', textAlign: 'center' }}>
                    <p style={{ fontSize: '0.82rem', fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.15em', marginBottom: '2rem' }}>
                        Empowering Smarter Decisions with Enterprise Architecture
                    </p>
                    <div style={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        gap: '4.5rem'
                    }} className="trust-grid">
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', opacity: 0.8 }}>
                            <span style={{ fontWeight: 800, color: '#475569', fontSize: '1.1rem' }}>Shopify Partner</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', opacity: 0.8 }}>
                            <svg style={{ width: '20px', height: '20px', color: '#3b82f6' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                            <span style={{ fontWeight: 700, color: '#475569', fontSize: '1.05rem' }}>Real-time Sync</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', opacity: 0.8 }}>
                            <svg style={{ width: '20px', height: '20px', color: '#10b981' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                            </svg>
                            <span style={{ fontWeight: 700, color: '#475569', fontSize: '1.05rem' }}>Secure Analytics</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', opacity: 0.8 }}>
                            <svg style={{ width: '20px', height: '20px', color: '#a855f7' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H3a2 2 0 01-2-2V5a2 2 0 012-2h16a2 2 0 012 2v14a2 2 0 01-2 2z" />
                            </svg>
                            <span style={{ fontWeight: 700, color: '#475569', fontSize: '1.05rem' }}>Enterprise Reports</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', opacity: 0.8 }}>
                            <span style={{ fontWeight: 700, color: '#475569', fontSize: '1.05rem' }}>⚡ Sub-second Speeds</span>
                        </div>
                    </div>
                </div>
            </section>

            {/* Core Features Grid */}
            <section id="features" style={{
                padding: '6.5rem 2rem',
                maxWidth: '1200px',
                margin: '0 auto',
                width: '100%',
                boxSizing: 'border-box'
            }}>
                <div style={{ textAlign: 'center', marginBottom: '4.5rem' }}>
                    <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: '0.12em', display: 'block', marginBottom: '0.75rem' }}>
                        Operational intelligence
                    </span>
                    <h2 style={{ fontSize: '2.5rem', fontWeight: 800, letterSpacing: '-0.025em', color: '#0f172a', margin: 0 }}>
                        Engineered for High-Profit Store Management
                    </h2>
                    <p style={{ color: '#475569', fontSize: '1.08rem', marginTop: '0.85rem', maxWidth: '620px', marginInline: 'auto', lineHeight: 1.6 }}>
                        Leave spreadsheets behind. Track margins, automate risk management, and predict customer lifecycles using powerful analytical models.
                    </p>
                </div>

                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))',
                    gap: '2rem'
                }}>
                    {/* Card 1 */}
                    <div className="glass-card" style={{ padding: '2.25rem', display: 'flex', flexDirection: 'column', gap: '1.2rem', borderRadius: '24px', background: 'rgba(255, 255, 255, 0.75)' }}>
                        <div style={{ width: '46px', height: '46px', borderRadius: '12px', background: 'rgba(99, 102, 241, 0.1)', color: '#6366f1', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '10px', boxSizing: 'border-box' }}>
                            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                            </svg>
                        </div>
                        <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700 }}>Performance Intelligence</h3>
                        <p style={{ margin: 0, color: '#475569', fontSize: '0.94rem', lineHeight: 1.6 }}>
                            Gain instant clarity over net sales, gross margin variations, exact payment gateways fees, and real-time operational costs.
                        </p>
                    </div>

                    {/* Card 2 */}
                    <div className="glass-card" style={{ padding: '2.25rem', display: 'flex', flexDirection: 'column', gap: '1.2rem', borderRadius: '24px', background: 'rgba(255, 255, 255, 0.75)' }}>
                        <div style={{ width: '46px', height: '46px', borderRadius: '12px', background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '10px', boxSizing: 'border-box' }}>
                            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                            </svg>
                        </div>
                        <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700 }}>Order Intelligence</h3>
                        <p style={{ margin: 0, color: '#475569', fontSize: '0.94rem', lineHeight: 1.6 }}>
                            Identify high-risk orders, track RTO (Return to Origin) probabilities, calculate refund frequencies, and detect unprofitable shipments instantly.
                        </p>
                    </div>

                    {/* Card 3 */}
                    <div className="glass-card" style={{ padding: '2.25rem', display: 'flex', flexDirection: 'column', gap: '1.2rem', borderRadius: '24px', background: 'rgba(255, 255, 255, 0.75)' }}>
                        <div style={{ width: '46px', height: '46px', borderRadius: '12px', background: 'rgba(59, 130, 246, 0.1)', color: '#3b82f6', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '10px', boxSizing: 'border-box' }}>
                            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                            </svg>
                        </div>
                        <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700 }}>Customer Hub</h3>
                        <p style={{ margin: 0, color: '#475569', fontSize: '0.94rem', lineHeight: 1.6 }}>
                            Discover your VIP buyers, monitor customer cohort retention rates, predict customer lifetime value, and isolate cohort trends.
                        </p>
                    </div>

                    {/* Card 4 */}
                    <div className="glass-card" style={{ padding: '2.25rem', display: 'flex', flexDirection: 'column', gap: '1.2rem', borderRadius: '24px', background: 'rgba(255, 255, 255, 0.75)' }}>
                        <div style={{ width: '46px', height: '46px', borderRadius: '12px', background: 'rgba(16, 185, 129, 0.1)', color: '#10b981', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '10px', boxSizing: 'border-box' }}>
                            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                            </svg>
                        </div>
                        <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700 }}>Product Intelligence</h3>
                        <p style={{ margin: 0, color: '#475569', fontSize: '0.94rem', lineHeight: 1.6 }}>
                            Manage inventory health, track product variant profit margins, trace returns by collection, and flag slow-moving stock before it hurts your cashflow.
                        </p>
                    </div>

                    {/* Card 5 */}
                    <div className="glass-card" style={{ padding: '2.25rem', display: 'flex', flexDirection: 'column', gap: '1.2rem', borderRadius: '24px', background: 'rgba(255, 255, 255, 0.75)' }}>
                        <div style={{ width: '46px', height: '46px', borderRadius: '12px', background: 'rgba(168, 85, 247, 0.1)', color: '#a855f7', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '10px', boxSizing: 'border-box' }}>
                            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
                            </svg>
                        </div>
                        <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700 }}>Advanced Segmentation</h3>
                        <p style={{ margin: 0, color: '#475569', fontSize: '0.94rem', lineHeight: 1.6 }}>
                            Generate custom marketing lists. Segment customers instantly by spending parameters, geography, product lines, and order statuses.
                        </p>
                    </div>

                    {/* Card 6 */}
                    <div className="glass-card" style={{ padding: '2.25rem', display: 'flex', flexDirection: 'column', gap: '1.2rem', borderRadius: '24px', background: 'rgba(255, 255, 255, 0.75)' }}>
                        <div style={{ width: '46px', height: '46px', borderRadius: '12px', background: 'rgba(236, 72, 153, 0.1)', color: '#ec4899', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '10px', boxSizing: 'border-box' }}>
                            <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                            </svg>
                        </div>
                        <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700 }}>Intelligence Center</h3>
                        <p style={{ margin: 0, color: '#475569', fontSize: '0.94rem', lineHeight: 1.6 }}>
                            Leverage advanced forecasting models to project future inventory needs, track advertising efficiency (CAC vs LTV), and compute overall store health scores.
                        </p>
                    </div>
                </div>
            </section>

            {/* Tabbed Dashboard Showcase */}
            <section id="showcase" style={{
                padding: '6rem 2rem',
                background: 'rgba(255, 255, 255, 0.65)',
                borderTop: '1px solid rgba(241, 245, 249, 0.9)',
                borderBottom: '1px solid rgba(241, 245, 249, 0.9)'
            }}>
                <div style={{ maxWidth: '1200px', margin: '0 auto', width: '100%' }}>
                    <div style={{ textAlign: 'center', marginBottom: '3.5rem' }}>
                        <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: '0.12em', display: 'block', marginBottom: '0.75rem' }}>
                            Live interface demo
                        </span>
                        <h2 style={{ fontSize: '2.4rem', fontWeight: 800, letterSpacing: '-0.025em', color: '#0f172a', margin: 0 }}>
                            Step Inside Tvora's Core Analytics Engine
                        </h2>
                        <p style={{ color: '#475569', fontSize: '1.05rem', marginTop: '0.8rem', maxWidth: '600px', marginInline: 'auto' }}>
                            Explore our interactive mock layouts matching exactly what you see when you connect your store.
                        </p>
                    </div>

                    {/* Tab Switcher Headers */}
                    <div className="tab-switcher" style={{
                        display: 'flex',
                        justifyContent: 'center',
                        flexWrap: 'wrap',
                        gap: '0.5rem',
                        marginBottom: '3rem',
                        background: 'rgba(241, 245, 249, 0.6)',
                        padding: '0.5rem',
                        borderRadius: '16px',
                        maxWidth: '900px',
                        marginInline: 'auto',
                        border: '1px solid rgba(226, 232, 240, 0.6)'
                    }}>
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                style={{
                                    padding: '0.65rem 1.15rem',
                                    borderRadius: '12px',
                                    border: 'none',
                                    fontWeight: 600,
                                    fontSize: '0.88rem',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s',
                                    background: activeTab === tab.id ? 'white' : 'transparent',
                                    color: activeTab === tab.id ? '#4f46e5' : '#475569',
                                    boxShadow: activeTab === tab.id ? '0 4px 12px rgba(15, 23, 42, 0.05)' : 'none'
                                }}
                            >
                                {tab.name}
                            </button>
                        ))}
                    </div>

                    {/* Active Mockup Container */}
                    <div className="glass-card" style={{
                        background: '#ffffff',
                        border: '1px solid rgba(226, 232, 240, 0.8)',
                        boxShadow: '0 30px 70px rgba(15, 23, 42, 0.07)',
                        borderRadius: '24px',
                        padding: '2rem',
                        minHeight: '400px'
                    }}>
                        <AnimatePresence mode="wait">
                            <motion.div
                                key={activeTab}
                                initial={{ opacity: 0, y: 15 }}
                                animate={{ opacity: 1, y: 0 }}
                                exit={{ opacity: 0, y: -15 }}
                                transition={{ duration: 0.3 }}
                            >
                                {activeTab === 'overview' && (
                                    <div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', marginBottom: '2rem' }}>
                                            <div>
                                                <span style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Store Dashboard</span>
                                                <h3 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 800 }}>Overview Intelligence</h3>
                                            </div>
                                            <div style={{ display: 'flex', gap: '0.5rem' }}>
                                                <span style={{ fontSize: '0.82rem', padding: '0.4rem 0.8rem', background: '#f1f5f9', borderRadius: '8px', color: '#475569', fontWeight: 600 }}>Last 30 Days</span>
                                                <span style={{ fontSize: '0.82rem', padding: '0.4rem 0.8rem', background: '#e0f2fe', borderRadius: '8px', color: '#0369a1', fontWeight: 600 }}>Real-time Enabled</span>
                                            </div>
                                        </div>

                                        {/* Key Stats Row */}
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.25rem', marginBottom: '2rem' }}>
                                            <div style={{ background: '#f8fafc', padding: '1.25rem', borderRadius: '16px', border: '1px solid rgba(0,0,0,0.02)' }}>
                                                <span style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 600 }}>Gross Sales</span>
                                                <h4 style={{ margin: '0.3rem 0', fontSize: '1.6rem', fontWeight: 900 }}>$148,930.20</h4>
                                                <span style={{ fontSize: '0.78rem', color: '#10b981', fontWeight: 600 }}>↑ +12.4% vs last period</span>
                                            </div>
                                            <div style={{ background: '#f8fafc', padding: '1.25rem', borderRadius: '16px', border: '1px solid rgba(0,0,0,0.02)' }}>
                                                <span style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 600 }}>Net Sales</span>
                                                <h4 style={{ margin: '0.3rem 0', fontSize: '1.6rem', fontWeight: 900 }}>$124,490.50</h4>
                                                <span style={{ fontSize: '0.78rem', color: '#10b981', fontWeight: 600 }}>↑ +14.2% profitability</span>
                                            </div>
                                            <div style={{ background: '#f8fafc', padding: '1.25rem', borderRadius: '16px', border: '1px solid rgba(0,0,0,0.02)' }}>
                                                <span style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 600 }}>Average Order Value (AOV)</span>
                                                <h4 style={{ margin: '0.3rem 0', fontSize: '1.6rem', fontWeight: 900 }}>$84.50</h4>
                                                <span style={{ fontSize: '0.78rem', color: '#4f46e5', fontWeight: 600 }}>Consistent average</span>
                                            </div>
                                            <div style={{ background: '#f8fafc', padding: '1.25rem', borderRadius: '16px', border: '1px solid rgba(0,0,0,0.02)' }}>
                                                <span style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 600 }}>Return Rate</span>
                                                <h4 style={{ margin: '0.3rem 0', fontSize: '1.6rem', fontWeight: 900 }}>3.4%</h4>
                                                <span style={{ fontSize: '0.78rem', color: '#10b981', fontWeight: 600 }}>↓ -1.2% this week</span>
                                            </div>
                                        </div>

                                        {/* Snapshot description */}
                                        <p style={{ color: '#475569', fontSize: '0.98rem', lineHeight: 1.6, margin: 0 }}>
                                            The **Overview Dashboard** provides an executive summary of your entire storefront. It computes gross sales, processes payment gateways fees, tracks overall return ratios, and visualizes sales trend progressions using real-time sync hooks.
                                        </p>
                                    </div>
                                )}

                                {activeTab === 'orders' && (
                                    <div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', marginBottom: '2rem' }}>
                                            <div>
                                                <span style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Prevention & Risks</span>
                                                <h3 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 800 }}>Order Intelligence Engine</h3>
                                            </div>
                                            <span style={{ fontSize: '0.8rem', padding: '0.35rem 0.75rem', background: '#fee2e2', color: '#ef4444', borderRadius: '8px', fontWeight: 700 }}>
                                                RTO Protection Active
                                            </span>
                                        </div>

                                        <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr', gap: '2rem', marginBottom: '1.5rem' }} className="mockup-split-grid">
                                            <div>
                                                <h4 style={{ margin: '0 0 1rem 0', fontSize: '1.1rem', fontWeight: 700 }}>Recent Risky Orders Flagged</h4>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.85rem 1rem', background: '#fff5f5', borderRadius: '12px', border: '1px solid rgba(239,68,68,0.1)' }}>
                                                        <div>
                                                            <div style={{ fontWeight: 700, fontSize: '0.88rem' }}>Order #18932 — $342.90</div>
                                                            <span style={{ fontSize: '0.75rem', color: '#64748b' }}>High shipping to billing address discrepancy</span>
                                                        </div>
                                                        <span style={{ fontSize: '0.72rem', background: '#ef4444', color: 'white', padding: '0.2rem 0.5rem', borderRadius: '6px', fontWeight: 700 }}>92% Risk</span>
                                                    </div>
                                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.85rem 1rem', background: '#fffaf0', borderRadius: '12px', border: '1px solid rgba(245,158,11,0.1)' }}>
                                                        <div>
                                                            <div style={{ fontWeight: 700, fontSize: '0.88rem' }}>Order #18929 — $84.00</div>
                                                            <span style={{ fontSize: '0.75rem', color: '#64748b' }}>Suspected RTO pattern (3 previous order cancellations)</span>
                                                        </div>
                                                        <span style={{ fontSize: '0.72rem', background: '#f59e0b', color: 'white', padding: '0.2rem 0.5rem', borderRadius: '6px', fontWeight: 700 }}>68% Risk</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div style={{ background: '#f8fafc', padding: '1.25rem', borderRadius: '16px' }}>
                                                <h4 style={{ margin: '0 0 0.85rem 0', fontSize: '1rem', fontWeight: 700 }}>Risk Metrics</h4>
                                                <ul style={{ paddingLeft: '1.2rem', margin: 0, fontSize: '0.88rem', color: '#475569', display: 'flex', flexDirection: 'column', gap: '0.6rem' }}>
                                                    <li><strong>High Risk Orders:</strong> 3 flagged today</li>
                                                    <li><strong>Loss-making Orders:</strong> 0 detected</li>
                                                    <li><strong>RTO Rate:</strong> 1.8% (Excellent)</li>
                                                    <li><strong>Refunding Rate:</strong> 2.4%</li>
                                                </ul>
                                            </div>
                                        </div>

                                        <p style={{ color: '#475569', fontSize: '0.98rem', lineHeight: 1.6, margin: 0 }}>
                                            The **Order Intelligence** dashboard tracks shipping anomalies, return statistics, and order cancellations. It computes an automated risk score for incoming Shopify orders so store owners can prevent high-risk RTO (Return to Origin) shipments before they leave the warehouse.
                                        </p>
                                    </div>
                                )}

                                {activeTab === 'customers' && (
                                    <div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', marginBottom: '2rem' }}>
                                            <div>
                                                <span style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Customer Hub</span>
                                                <h3 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 800 }}>Retention & RFM Segmentation</h3>
                                            </div>
                                            <span style={{ fontSize: '0.8rem', padding: '0.4rem 0.8rem', background: '#f3e8ff', color: '#a855f7', borderRadius: '8px', fontWeight: 700 }}>
                                                3,492 Profiles Tracked
                                            </span>
                                        </div>

                                        {/* RFM Blocks */}
                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1.25rem', marginBottom: '2rem' }}>
                                            <div style={{ borderLeft: '4px solid #a855f7', background: '#faf5ff', padding: '1rem', borderRadius: '0 12px 12px 0' }}>
                                                <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>Champions</div>
                                                <div style={{ fontSize: '1.25rem', fontWeight: 800, margin: '0.2rem 0' }}>248 Buyers</div>
                                                <span style={{ fontSize: '0.75rem', color: '#7c3aed' }}>LTV Range: &gt; $800</span>
                                            </div>
                                            <div style={{ borderLeft: '4px solid #3b82f6', background: '#eff6ff', padding: '1rem', borderRadius: '0 12px 12px 0' }}>
                                                <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>Loyal Customers</div>
                                                <div style={{ fontSize: '1.25rem', fontWeight: 800, margin: '0.2rem 0' }}>842 Buyers</div>
                                                <span style={{ fontSize: '0.75rem', color: '#2563eb' }}>Order Frequency: 3-5 orders</span>
                                            </div>
                                            <div style={{ borderLeft: '4px solid #f59e0b', background: '#fffbeb', padding: '1rem', borderRadius: '0 12px 12px 0' }}>
                                                <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>At-Risk VIPs</div>
                                                <div style={{ fontSize: '1.25rem', fontWeight: 800, margin: '0.2rem 0' }}>112 Buyers</div>
                                                <span style={{ fontSize: '0.75rem', color: '#d97706' }}>No purchase in 60 days</span>
                                            </div>
                                        </div>

                                        <p style={{ color: '#475569', fontSize: '0.98rem', lineHeight: 1.6, margin: 0 }}>
                                            The **Customer Hub** calculates dynamic customer cohort retention patterns, monitors acquisition sources, filters out dormant buyers, and offers Recency-Frequency-Monetary metrics to power customized retention sequences.
                                        </p>
                                    </div>
                                )}

                                {activeTab === 'products' && (
                                    <div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', marginBottom: '2rem' }}>
                                            <div>
                                                <span style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Inventory Analytics</span>
                                                <h3 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 800 }}>Product Intelligence</h3>
                                            </div>
                                            <span style={{ fontSize: '0.8rem', padding: '0.4rem 0.8rem', background: '#ecfdf5', color: '#059669', borderRadius: '8px', fontWeight: 700 }}>
                                                Optimal Stock Levels
                                            </span>
                                        </div>

                                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', marginBottom: '1.5rem' }} className="mockup-split-grid">
                                            <div style={{ background: '#f8fafc', padding: '1.25rem', borderRadius: '16px' }}>
                                                <h4 style={{ margin: '0 0 0.85rem 0', fontSize: '1rem', fontWeight: 700, color: '#1e293b' }}>Top Margin Contributors</h4>
                                                <ul style={{ padding: 0, listStyle: 'none', margin: 0, display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                                    <li style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.88rem' }}>
                                                        <span>Premium Leather Tote</span>
                                                        <strong style={{ color: '#10b981' }}>72% Margin ($112 profit)</strong>
                                                    </li>
                                                    <li style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.88rem' }}>
                                                        <span>Wireless Charging Hub</span>
                                                        <strong style={{ color: '#10b981' }}>65% Margin ($34 profit)</strong>
                                                    </li>
                                                </ul>
                                            </div>
                                            <div style={{ background: '#f8fafc', padding: '1.25rem', borderRadius: '16px' }}>
                                                <h4 style={{ margin: '0 0 0.85rem 0', fontSize: '1rem', fontWeight: 700, color: '#1e293b' }}>Low Stock Alerts</h4>
                                                <ul style={{ padding: 0, listStyle: 'none', margin: 0, display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                                                    <li style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.88rem' }}>
                                                        <span>Minimalist Cardholder</span>
                                                        <span style={{ color: '#ef4444', fontWeight: 600 }}>12 left (Runout in 4 days)</span>
                                                    </li>
                                                    <li style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.88rem' }}>
                                                        <span>Premium Silk Scarf</span>
                                                        <span style={{ color: '#f59e0b', fontWeight: 600 }}>24 left (Runout in 11 days)</span>
                                                    </li>
                                                </ul>
                                            </div>
                                        </div>

                                        <p style={{ color: '#475569', fontSize: '0.98rem', lineHeight: 1.6, margin: 0 }}>
                                            The **Product Intelligence** dashboard gives inventory managers high-precision margin calculators, collections performance insights, return rates per product line, and real-time inventory levels to avoid stock-outs.
                                        </p>
                                    </div>
                                )}

                                {activeTab === 'segments' && (
                                    <div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', marginBottom: '2rem' }}>
                                            <div>
                                                <span style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Smart Lists</span>
                                                <h3 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 800 }}>Advanced Segmentation Engine</h3>
                                            </div>
                                            <button style={{
                                                background: '#4f46e5',
                                                color: 'white',
                                                border: 'none',
                                                padding: '0.45rem 1rem',
                                                borderRadius: '8px',
                                                fontWeight: 600,
                                                fontSize: '0.82rem',
                                                cursor: 'pointer'
                                            }}>
                                                Export Segment CSV
                                            </button>
                                        </div>

                                        {/* Query Builder Mockup */}
                                        <div style={{ background: '#f8fafc', padding: '1.5rem', borderRadius: '16px', border: '1px solid rgba(0,0,0,0.03)', marginBottom: '1.5rem' }}>
                                            <div style={{ fontSize: '0.82rem', color: '#64748b', fontWeight: 700, marginBottom: '0.85rem' }}>Active Segmentation Rule:</div>
                                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                                                <span style={{ background: '#e2e8f0', padding: '0.35rem 0.75rem', borderRadius: '8px', fontSize: '0.82rem', fontWeight: 600 }}>Total Spent &gt; $500</span>
                                                <span style={{ fontSize: '0.82rem', color: '#64748b', fontWeight: 700 }}>AND</span>
                                                <span style={{ background: '#e2e8f0', padding: '0.35rem 0.75rem', borderRadius: '8px', fontSize: '0.82rem', fontWeight: 600 }}>Order Count &gt;= 3</span>
                                                <span style={{ fontSize: '0.82rem', color: '#64748b', fontWeight: 700 }}>AND</span>
                                                <span style={{ background: '#e2e8f0', padding: '0.35rem 0.75rem', borderRadius: '8px', fontSize: '0.82rem', fontWeight: 600 }}>Country == United States</span>
                                            </div>
                                            <div style={{ marginTop: '1.25rem', fontSize: '0.85rem', color: '#059669', fontWeight: 700 }}>
                                                ✓ Matches 312 customers in your database.
                                            </div>
                                        </div>

                                        <p style={{ color: '#475569', fontSize: '0.98rem', lineHeight: 1.6, margin: 0 }}>
                                            The **Segmentation Engine** offers Shopify store owners multi-conditional rule builders to extract customer datasets based on product tags, geographical tags, average values, or purchase frequency rules.
                                        </p>
                                    </div>
                                )}

                                {activeTab === 'intel' && (
                                    <div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', marginBottom: '2rem' }}>
                                            <div>
                                                <span style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Store Health & Forecasts</span>
                                                <h3 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 800 }}>Intelligence Center</h3>
                                            </div>
                                            <span style={{ fontSize: '0.85rem', padding: '0.4rem 0.8rem', background: '#eff6ff', color: '#1d4ed8', borderRadius: '8px', fontWeight: 800 }}>
                                                Health Score: 92/100
                                            </span>
                                        </div>

                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1.25rem', marginBottom: '2rem' }}>
                                            <div style={{ background: '#f8fafc', padding: '1.2rem', borderRadius: '16px' }}>
                                                <span style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 600 }}>Predicted Sales (Next Month)</span>
                                                <h4 style={{ margin: '0.2rem 0', fontSize: '1.4rem', fontWeight: 800, color: '#1e293b' }}>$164,280.00</h4>
                                                <span style={{ fontSize: '0.72rem', color: '#10b981', fontWeight: 700 }}>Confidence: 94%</span>
                                            </div>
                                            <div style={{ background: '#f8fafc', padding: '1.2rem', borderRadius: '16px' }}>
                                                <span style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 600 }}>Marketing LTV to CAC Ratio</span>
                                                <h4 style={{ margin: '0.2rem 0', fontSize: '1.4rem', fontWeight: 800, color: '#1e293b' }}>4.8x</h4>
                                                <span style={{ fontSize: '0.72rem', color: '#10b981', fontWeight: 700 }}>Highly profitable scaling</span>
                                            </div>
                                            <div style={{ background: '#f8fafc', padding: '1.2rem', borderRadius: '16px' }}>
                                                <span style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 600 }}>RTO Loss Prevented</span>
                                                <h4 style={{ margin: '0.2rem 0', fontSize: '1.4rem', fontWeight: 800, color: '#1e293b' }}>$4,930.00</h4>
                                                <span style={{ fontSize: '0.72rem', color: '#4f46e5', fontWeight: 700 }}>Via automatic alerts</span>
                                            </div>
                                        </div>

                                        <p style={{ color: '#475569', fontSize: '0.98rem', lineHeight: 1.6, margin: 0 }}>
                                            The **Intelligence Center** aggregates key growth indicators: it runs predictive models to forecast next month's sales, measures total marketing return parameters, and provides actionable store improvements.
                                        </p>
                                    </div>
                                )}

                                {activeTab === 'settings' && (
                                    <div>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', marginBottom: '2rem' }}>
                                            <div>
                                                <span style={{ fontSize: '0.78rem', color: '#64748b', fontWeight: 600, textTransform: 'uppercase' }}>Configurations</span>
                                                <h3 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 800 }}>Settings & Cost Configurator</h3>
                                            </div>
                                            <span style={{ fontSize: '0.8rem', padding: '0.4rem 0.8rem', background: '#f1f5f9', color: '#475569', borderRadius: '8px', fontWeight: 700 }}>
                                                GDPR Compliant
                                            </span>
                                        </div>

                                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }} className="mockup-split-grid">
                                            <div style={{ border: '1px solid rgba(226,232,240,0.8)', padding: '1.25rem', borderRadius: '16px', background: '#f8fafc' }}>
                                                <div style={{ fontSize: '0.85rem', fontWeight: 700, marginBottom: '0.6rem' }}>Cost of Goods Sold (COGS) Profiles</div>
                                                <span style={{ fontSize: '0.78rem', color: '#64748b', display: 'block', marginBottom: '0.85rem' }}>Define default cost rules or upload variants cost spreadsheets.</span>
                                                <button style={{ background: '#0f172a', color: 'white', border: 'none', padding: '0.45rem 1rem', borderRadius: '8px', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer' }}>Manage COGS</button>
                                            </div>
                                            <div style={{ border: '1px solid rgba(226,232,240,0.8)', padding: '1.25rem', borderRadius: '16px', background: '#f8fafc' }}>
                                                <div style={{ fontSize: '0.85rem', fontWeight: 700, marginBottom: '0.6rem' }}>Custom Shipping & Gateways Fee Settings</div>
                                                <span style={{ fontSize: '0.78rem', color: '#64748b', display: 'block', marginBottom: '0.85rem' }}>Configure exact gateway percentages (Stripe, Paypal) to refine margin calculations.</span>
                                                <button style={{ background: '#0f172a', color: 'white', border: 'none', padding: '0.45rem 1rem', borderRadius: '8px', fontSize: '0.82rem', fontWeight: 600, cursor: 'pointer' }}>Edit Gateways</button>
                                            </div>
                                        </div>

                                        <p style={{ color: '#475569', fontSize: '0.98rem', lineHeight: 1.6, margin: 0 }}>
                                            The **Settings** panel empowers store administrators to setup standard currencies, link Google / Meta Ad accounts, assign Cost of Goods sold (COGS) to product variants, and set alerts thresholds.
                                        </p>
                                    </div>
                                )}
                            </motion.div>
                        </AnimatePresence>
                    </div>
                </div>
            </section>

            {/* Target Audience / Who Is It For */}
            <section id="who-is-it-for" style={{
                padding: '6.5rem 2rem',
                maxWidth: '1200px',
                margin: '0 auto',
                width: '100%',
                boxSizing: 'border-box'
            }}>
                <div style={{ textAlign: 'center', marginBottom: '4.5rem' }}>
                    <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: '0.12em', display: 'block', marginBottom: '0.75rem' }}>
                        Ideal partners
                    </span>
                    <h2 style={{ fontSize: '2.4rem', fontWeight: 800, letterSpacing: '-0.025em', color: '#0f172a', margin: 0 }}>
                        Built For High-Growth Shopify Stores
                    </h2>
                    <p style={{ color: '#475569', fontSize: '1.05rem', marginTop: '0.8rem', maxWidth: '600px', marginInline: 'auto' }}>
                        Tvora adapts to diverse merchant types to optimize pricing profiles, protect shipments, and measure client cohorts.
                    </p>
                </div>

                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
                    gap: '1.75rem'
                }}>
                    <div style={{ background: 'white', border: '1px solid rgba(226, 232, 240, 0.8)', padding: '2rem', borderRadius: '20px', boxShadow: '0 8px 30px rgba(0,0,0,0.01)' }}>
                        <h4 style={{ margin: '0 0 0.85rem 0', fontSize: '1.15rem', fontWeight: 700 }}>D2C Brands</h4>
                        <p style={{ margin: 0, color: '#475569', fontSize: '0.92rem', lineHeight: 1.55 }}>
                            Identify high-margin lines and analyze repeat buyer intervals to increase your customer lifetime values.
                        </p>
                    </div>
                    <div style={{ background: 'white', border: '1px solid rgba(226, 232, 240, 0.8)', padding: '2rem', borderRadius: '20px', boxShadow: '0 8px 30px rgba(0,0,0,0.01)' }}>
                        <h4 style={{ margin: '0 0 0.85rem 0', fontSize: '1.15rem', fontWeight: 700 }}>Fashion & Apparel</h4>
                        <p style={{ margin: 0, color: '#475569', fontSize: '0.92rem', lineHeight: 1.55 }}>
                            Isolate size-based return rates and trace margins across seasonal collections.
                        </p>
                    </div>
                    <div style={{ background: 'white', border: '1px solid rgba(226, 232, 240, 0.8)', padding: '2rem', borderRadius: '20px', boxShadow: '0 8px 30px rgba(0,0,0,0.01)' }}>
                        <h4 style={{ margin: '0 0 0.85rem 0', fontSize: '1.15rem', fontWeight: 700 }}>Beauty & Cosmetics</h4>
                        <p style={{ margin: 0, color: '#475569', fontSize: '0.92rem', lineHeight: 1.55 }}>
                            Measure retention intervals and automate reorder alerts for replenishment products.
                        </p>
                    </div>
                    <div style={{ background: 'white', border: '1px solid rgba(226, 232, 240, 0.8)', padding: '2rem', borderRadius: '20px', boxShadow: '0 8px 30px rgba(0,0,0,0.01)' }}>
                        <h4 style={{ margin: '0 0 0.85rem 0', fontSize: '1.15rem', fontWeight: 700 }}>Consumer Electronics</h4>
                        <p style={{ margin: 0, color: '#475569', fontSize: '0.92rem', lineHeight: 1.55 }}>
                            Monitor high-value order risk scoring and lower checkout frauds.
                        </p>
                    </div>
                    <div style={{ background: 'white', border: '1px solid rgba(226, 232, 240, 0.8)', padding: '2rem', borderRadius: '20px', boxShadow: '0 8px 30px rgba(0,0,0,0.01)' }}>
                        <h4 style={{ margin: '0 0 0.85rem 0', fontSize: '1.15rem', fontWeight: 700 }}>Multi-product Stores</h4>
                        <p style={{ margin: 0, color: '#475569', fontSize: '0.92rem', lineHeight: 1.55 }}>
                            Audit hundreds of SKU variants margins, tracking inventory velocity and stock out warnings.
                        </p>
                    </div>
                    <div style={{ background: 'white', border: '1px solid rgba(226, 232, 240, 0.8)', padding: '2rem', borderRadius: '20px', boxShadow: '0 8px 30px rgba(0,0,0,0.01)' }}>
                        <h4 style={{ margin: '0 0 0.85rem 0', fontSize: '1.15rem', fontWeight: 700 }}>Scaling Businesses</h4>
                        <p style={{ margin: 0, color: '#475569', fontSize: '0.92rem', lineHeight: 1.55 }}>
                            Rely on automated reports, cohort filters, and cloudflared-fast response layouts.
                        </p>
                    </div>
                </div>
            </section>

            {/* How It Works */}
            <section style={{
                padding: '6rem 2rem',
                background: 'rgba(255, 255, 255, 0.5)',
                borderTop: '1px solid rgba(241, 245, 249, 0.9)'
            }}>
                <div style={{ maxWidth: '1000px', margin: '0 auto', width: '100%' }}>
                    <div style={{ textAlign: 'center', marginBottom: '4.5rem' }}>
                        <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: '0.12em', display: 'block', marginBottom: '0.75rem' }}>
                            Seamless setup
                        </span>
                        <h2 style={{ fontSize: '2.4rem', fontWeight: 800, letterSpacing: '-0.025em', color: '#0f172a', margin: 0 }}>
                            Three Steps to Smarter Store Margins
                        </h2>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '2.5rem' }}>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '1rem' }}>
                            <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: 'linear-gradient(135deg, #6366f1 0%, #3b82f6 100%)', color: 'white', fontWeight: 800, fontSize: '1.4rem', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 20px rgba(99,102,241,0.2)' }}>
                                1
                            </div>
                            <h4 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 700 }}>Install App</h4>
                            <p style={{ margin: 0, color: '#475569', fontSize: '0.92rem', lineHeight: 1.5 }}>
                                Link your Shopify store securely via our partner portal in under 1 minute.
                            </p>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '1rem' }}>
                            <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: 'linear-gradient(135deg, #6366f1 0%, #3b82f6 100%)', color: 'white', fontWeight: 800, fontSize: '1.4rem', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 20px rgba(99,102,241,0.2)' }}>
                                2
                            </div>
                            <h4 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 700 }}>Sync Store Data</h4>
                            <p style={{ margin: 0, color: '#475569', fontSize: '0.92rem', lineHeight: 1.5 }}>
                                Our servers compile historical checkouts, configure currencies, and fetch webhook connections.
                            </p>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center', gap: '1rem' }}>
                            <div style={{ width: '56px', height: '56px', borderRadius: '50%', background: 'linear-gradient(135deg, #6366f1 0%, #3b82f6 100%)', color: 'white', fontWeight: 800, fontSize: '1.4rem', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 20px rgba(99,102,241,0.2)' }}>
                                3
                            </div>
                            <h4 style={{ margin: 0, fontSize: '1.2rem', fontWeight: 700 }}>Start Growing Smarter</h4>
                            <p style={{ margin: 0, color: '#475569', fontSize: '0.92rem', lineHeight: 1.5 }}>
                                Log in to track exact net profit margin ratios, cohort values, and reduce RTO costs.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Testimonials */}
            <section id="testimonials" style={{
                padding: '6.5rem 2rem',
                maxWidth: '1200px',
                margin: '0 auto',
                width: '100%',
                boxSizing: 'border-box'
            }}>
                <div style={{ textAlign: 'center', marginBottom: '4.5rem' }}>
                    <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: '0.12em', display: 'block', marginBottom: '0.75rem' }}>
                        Merchant Success
                    </span>
                    <h2 style={{ fontSize: '2.4rem', fontWeight: 800, letterSpacing: '-0.025em', color: '#0f172a', margin: 0 }}>
                        Trusted By Leading Shopify Brands
                    </h2>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '2rem' }}>
                    <div style={{ background: 'white', border: '1px solid rgba(226,232,240,0.8)', padding: '2.25rem', borderRadius: '24px', boxShadow: '0 10px 30px rgba(0,0,0,0.01)' }}>
                        <div style={{ display: 'flex', gap: '0.15rem', color: '#fbbf24', marginBottom: '1rem' }}>
                            {"★★★★★".split("").map((s, i) => <span key={i}>{s}</span>)}
                        </div>
                        <p style={{ color: '#475569', fontSize: '0.95rem', lineHeight: 1.6, fontStyle: 'italic', margin: '0 0 1.5rem 0' }}>
                            "Tvora changed how we run our operations. Before this, calculating COGS and deducting PayPal/Stripe processing costs took hours. Now we see exact net margins update live."
                        </p>
                        <div>
                            <strong style={{ display: 'block', fontSize: '0.92rem' }}>Sarah Jenkins</strong>
                            <span style={{ fontSize: '0.78rem', color: '#64748b' }}>Founder, Elevate Fashion (32% YoY growth)</span>
                        </div>
                    </div>

                    <div style={{ background: 'white', border: '1px solid rgba(226,232,240,0.8)', padding: '2.25rem', borderRadius: '24px', boxShadow: '0 10px 30px rgba(0,0,0,0.01)' }}>
                        <div style={{ display: 'flex', gap: '0.15rem', color: '#fbbf24', marginBottom: '1rem' }}>
                            {"★★★★★".split("").map((s, i) => <span key={i}>{s}</span>)}
                        </div>
                        <p style={{ color: '#475569', fontSize: '0.95rem', lineHeight: 1.6, fontStyle: 'italic', margin: '0 0 1.5rem 0' }}>
                            "The Order Intelligence component saved us thousands. It flags high-risk COD orders with bad addresses automatically, lowering our return rate to a record low of 1.4%."
                        </p>
                        <div>
                            <strong style={{ display: 'block', fontSize: '0.92rem' }}>Devon Carter</strong>
                            <span style={{ fontSize: '0.78rem', color: '#64748b' }}>Operations Manager, Aura Cosmetics</span>
                        </div>
                    </div>

                    <div style={{ background: 'white', border: '1px solid rgba(226,232,240,0.8)', padding: '2.25rem', borderRadius: '24px', boxShadow: '0 10px 30px rgba(0,0,0,0.01)' }}>
                        <div style={{ display: 'flex', gap: '0.15rem', color: '#fbbf24', marginBottom: '1rem' }}>
                            {"★★★★★".split("").map((s, i) => <span key={i}>{s}</span>)}
                        </div>
                        <p style={{ color: '#475569', fontSize: '0.95rem', lineHeight: 1.6, fontStyle: 'italic', margin: '0 0 1.5rem 0' }}>
                            "We use the Segmentation Engine to extract VIP cohorts and export them straight to our email flows. Retention improved by 24% in the first two months."
                        </p>
                        <div>
                            <strong style={{ display: 'block', fontSize: '0.92rem' }}>Marcus Chen</strong>
                            <span style={{ fontSize: '0.78rem', color: '#64748b' }}>Growth Lead, Core Gadgets</span>
                        </div>
                    </div>
                </div>
            </section>



            {/* FAQ Section */}
            <section id="faq" style={{
                padding: '6.5rem 2rem',
                maxWidth: '900px',
                margin: '0 auto',
                width: '100%',
                boxSizing: 'border-box'
            }}>
                <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
                    <span style={{ fontSize: '0.82rem', fontWeight: 700, color: '#6366f1', textTransform: 'uppercase', letterSpacing: '0.12em', display: 'block', marginBottom: '0.75rem' }}>
                        Common inquiries
                    </span>
                    <h2 style={{ fontSize: '2.4rem', fontWeight: 800, letterSpacing: '-0.025em', color: '#0f172a', margin: 0 }}>
                        Frequently Asked Questions
                    </h2>
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    {faqs.map((faq, idx) => (
                        <div
                            key={idx}
                            style={{
                                background: 'white',
                                border: '1px solid rgba(226, 232, 240, 0.8)',
                                borderRadius: '16px',
                                overflow: 'hidden',
                                transition: 'all 0.2s'
                            }}
                        >
                            <button
                                onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                                style={{
                                    width: '100%',
                                    padding: '1.25rem 1.5rem',
                                    border: 'none',
                                    background: 'transparent',
                                    textAlign: 'left',
                                    fontSize: '1rem',
                                    fontWeight: 700,
                                    color: '#1e293b',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center'
                                }}
                            >
                                <span>{faq.q}</span>
                                <span style={{
                                    transform: openFaq === idx ? 'rotate(180deg)' : 'none',
                                    transition: 'transform 0.2s',
                                    fontSize: '1.2rem',
                                    color: '#64748b'
                                }}>
                                    ▾
                                </span>
                            </button>
                            {openFaq === idx && (
                                <div style={{
                                    padding: '0 1.5rem 1.25rem 1.5rem',
                                    fontSize: '0.92rem',
                                    lineHeight: 1.6,
                                    color: '#475569',
                                    borderTop: '1px solid rgba(226,232,240,0.4)'
                                }}>
                                    {faq.a}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </section>

            {/* Final CTA Section */}
            <section style={{
                padding: '6.5rem 2rem',
                position: 'relative',
                background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)',
                color: 'white',
                textAlign: 'center',
                overflow: 'hidden'
            }}>
                {/* Background decorative circles */}
                <div style={{ position: 'absolute', top: '-50%', left: '-20%', width: '400px', height: '400px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(99,102,241,0.1) 0%, rgba(255,255,255,0) 70%)', filter: 'blur(30px)' }} />
                <div style={{ position: 'absolute', bottom: '-50%', right: '-20%', width: '400px', height: '400px', borderRadius: '50%', background: 'radial-gradient(circle, rgba(59,130,246,0.1) 0%, rgba(255,255,255,0) 70%)', filter: 'blur(30px)' }} />

                <div style={{ maxWidth: '800px', margin: '0 auto', position: 'relative', zIndex: 1 }}>
                    <h2 style={{ fontSize: '2.8rem', fontWeight: 900, letterSpacing: '-0.03em', margin: '0 0 1.25rem 0', lineHeight: 1.15 }}>
                        Start Scaling With Smarter Analytics
                    </h2>
                    <p style={{ fontSize: '1.15rem', color: '#94a3b8', lineHeight: 1.6, margin: '0 0 2.5rem 0', maxWidth: '600px', marginInline: 'auto' }}>
                        Join thousands of Shopify merchants using Tvora to secure profit margins, prevent RTO costs, and retain VIP clients.
                    </p>
                    <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
                        <a href="#install" style={{
                            background: 'linear-gradient(135deg, #6366f1 0%, #3b82f6 100%)',
                            color: 'white',
                            padding: '0.85rem 2rem',
                            borderRadius: '14px',
                            textDecoration: 'none',
                            fontWeight: 700,
                            fontSize: '1rem',
                            boxShadow: '0 10px 25px rgba(99, 102, 241, 0.3)'
                        }}>
                            Start Free Trial
                        </a>
                        <a href="#install" style={{
                            background: 'rgba(255, 255, 255, 0.1)',
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            color: 'white',
                            padding: '0.85rem 2rem',
                            borderRadius: '14px',
                            textDecoration: 'none',
                            fontWeight: 700,
                            fontSize: '1rem'
                        }}>
                            Install on Shopify
                        </a>
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer style={{
                background: '#090d16',
                color: '#64748b',
                padding: '4rem 2rem 3rem 2rem',
                borderTop: '1px solid rgba(255,255,255,0.05)'
            }}>
                <div style={{ maxWidth: '1200px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '3rem' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '2.5rem' }}>
                        <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
                                <img 
                                    src={logoImg} 
                                    alt="Tvora Logo" 
                                    style={{
                                        width: '32px',
                                        height: '32px',
                                        borderRadius: '8px',
                                        objectFit: 'cover'
                                    }} 
                                />
                                <span style={{ fontWeight: 800, fontSize: '1.25rem', color: 'white' }}>Tvora</span>
                            </div>
                            <p style={{ fontSize: '0.88rem', lineHeight: 1.5, maxWidth: '280px', margin: 0 }}>
                                Complete Business Intelligence & Profit Operating System for scaling Shopify stores.
                            </p>
                        </div>

                        <div style={{ display: 'flex', gap: '4rem', flexWrap: 'wrap' }}>
                            <div>
                                <h5 style={{ color: 'white', margin: '0 0 1rem 0', fontSize: '0.9rem', fontWeight: 700 }}>Product</h5>
                                <ul style={{ padding: 0, margin: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.65rem', fontSize: '0.85rem' }}>
                                    <li><a href="#features" onClick={(e) => handleScroll(e, 'features')} style={{ color: '#64748b', textDecoration: 'none' }}>Features</a></li>
                                    <li><a href="#showcase" onClick={(e) => handleScroll(e, 'showcase')} style={{ color: '#64748b', textDecoration: 'none' }}>Dashboard Showcase</a></li>

                                </ul>
                            </div>
                            <div>
                                <h5 style={{ color: 'white', margin: '0 0 1rem 0', fontSize: '0.9rem', fontWeight: 700 }}>Resources</h5>
                                <ul style={{ padding: 0, margin: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: '0.65rem', fontSize: '0.85rem' }}>
                                    <li><a href="#faq" onClick={(e) => handleScroll(e, 'faq')} style={{ color: '#64748b', textDecoration: 'none' }}>FAQ</a></li>
                                    <li><a href="/support" onClick={(e) => { e.preventDefault(); navigate('/support'); }} style={{ color: '#64748b', textDecoration: 'none' }}>Support Contact</a></li>
                                    <li><a href="/privacy" onClick={(e) => { e.preventDefault(); navigate('/privacy'); }} style={{ color: '#64748b', textDecoration: 'none' }}>Privacy Policy</a></li>
                                    <li><a href="/terms" onClick={(e) => { e.preventDefault(); navigate('/terms'); }} style={{ color: '#64748b', textDecoration: 'none' }}>Terms of Service</a></li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem', borderTop: '1px solid rgba(255,255,255,0.05)', paddingTop: '2rem', fontSize: '0.82rem' }}>
                        <span>© {new Date().getFullYear()} Tvora: Analytics & Profit. All rights reserved.</span>
                        <span>Need assistance? Contact <a href="mailto:support@tvora.co" style={{ color: '#6366f1', textDecoration: 'none', fontWeight: 600 }}>support@tvora.co</a></span>
                    </div>
                </div>
            </footer>

            {/* Mobile/Desktop responsive fixes using dynamic style blocks */}
            <style dangerouslySetInnerHTML={{__html: `
                /* Global styling overrides */
                *, *::before, *::after {
                    box-sizing: border-box !important;
                }

                html, body {
                    max-width: 100% !important;
                    overflow-x: hidden !important;
                    width: 100% !important;
                }
                
                section[id], #install {
                    scroll-margin-top: 85px !important;
                }

                /* Word wrap for all text */
                h1, h2, h3, h4, h5, h6, p, span, a, li {
                    word-break: break-word !important;
                    overflow-wrap: break-word !important;
                }

                /* Hide hamburger on desktop */
                .hamburger-button {
                    display: none !important;
                }

                /* ===== TABLET: 968px ===== */
                @media (max-width: 968px) {
                    .hero-grid {
                        grid-template-columns: 1fr !important;
                        gap: 2.5rem !important;
                        text-align: center !important;
                    }
                    .hero-grid h1 {
                        font-size: 2.3rem !important;
                        line-height: 1.15 !important;
                    }
                    .hero-grid p {
                        margin-inline: auto !important;
                        font-size: 1.02rem !important;
                    }
                    .hero-grid div[style*="display: flex"][style*="gap: 1rem"] {
                        justify-content: center !important;
                    }
                    .hero-graphics {
                        height: 300px !important;
                        display: flex !important;
                        justify-content: center !important;
                        align-items: center !important;
                        overflow: hidden !important;
                    }
                    .hero-graphics > div {
                        position: relative !important;
                        right: auto !important;
                        left: auto !important;
                        top: auto !important;
                        bottom: auto !important;
                        transform: none !important;
                        max-width: 100% !important;
                        width: 100% !important;
                    }
                    .hero-graphics > div:not(:first-child) {
                        display: none !important;
                    }
                    .trust-grid {
                        gap: 2rem !important;
                    }
                    .mockup-split-grid {
                        grid-template-columns: 1fr !important;
                        gap: 1.5rem !important;
                    }
                    .nav-desktop {
                        display: none !important;
                    }
                    section {
                        padding: 3.5rem 1.15rem !important;
                    }
                }

                /* ===== MOBILE: 768px ===== */
                @media (max-width: 768px) {
                    /* Header */
                    .landing-header {
                        padding: 0.85rem 1.25rem !important;
                        flex-direction: row !important;
                        justify-content: space-between !important;
                        align-items: center !important;
                        min-height: 64px !important;
                        box-sizing: border-box !important;
                    }
                    .landing-header .header-logo {
                        width: auto !important;
                        justify-content: flex-start !important;
                    }
                    .landing-header .header-actions {
                        display: none !important;
                    }
                    .landing-header .hamburger-button {
                        display: flex !important;
                        margin-left: auto !important;
                    }

                    /* Tab switcher scrollable */
                    .tab-switcher {
                        justify-content: flex-start !important;
                        overflow-x: auto !important;
                        white-space: nowrap !important;
                        flex-wrap: nowrap !important;
                        padding-bottom: 0.75rem !important;
                        padding-inline: 0.75rem !important;
                        gap: 0.4rem !important;
                        -webkit-overflow-scrolling: touch !important;
                    }
                    .tab-switcher::-webkit-scrollbar {
                        height: 4px;
                    }
                    .tab-switcher::-webkit-scrollbar-thumb {
                        background: rgba(99, 102, 241, 0.2);
                        border-radius: 10px;
                    }
                    .tab-switcher button {
                        flex: 0 0 auto !important;
                        font-size: 0.8rem !important;
                        padding: 0.5rem 0.9rem !important;
                    }

                    /* Install form */
                    #install {
                        padding: 1.25rem !important;
                        margin-inline: auto !important;
                        width: 100% !important;
                        box-sizing: border-box !important;
                    }
                    #install form {
                        flex-direction: column !important;
                        gap: 0.65rem !important;
                    }
                    #install button {
                        padding: 0.8rem !important;
                        width: 100% !important;
                    }

                    /* Typography */
                    .hero-grid h1 {
                        font-size: 2rem !important;
                    }
                    h2 {
                        font-size: 1.75rem !important;
                    }

                    /* Grid cards */
                    #features > div:nth-child(2),
                    #who-is-it-for > div:nth-child(2),
                    #testimonials > div:nth-child(2) {
                        grid-template-columns: 1fr !important;
                    }

                    /* CTA buttons */
                    section[style*="linear-gradient"] div[style*="display: flex"] {
                        flex-direction: column !important;
                        align-items: center !important;
                        gap: 0.75rem !important;
                    }
                    section[style*="linear-gradient"] div[style*="display: flex"] a {
                        width: 100% !important;
                        max-width: 280px !important;
                        box-sizing: border-box !important;
                    }

                    /* Show hero widget cards at 768px with adjusted positioning */
                    .hero-graphics {
                        height: 420px !important;
                        position: relative !important;
                        overflow: visible !important;
                    }
                    .hero-graphics > div:nth-child(2) {
                        display: block !important;
                        position: absolute !important;
                        bottom: 2% !important;
                        left: 2% !important;
                        top: auto !important;
                        max-width: 210px !important;
                        width: 210px !important;
                    }
                    .hero-graphics > div:nth-child(3) {
                        display: block !important;
                        position: absolute !important;
                        top: 38% !important;
                        left: 0% !important;
                        max-width: 195px !important;
                        width: 195px !important;
                    }
                }

                /* ===== SMALL MOBILE: 480px ===== */
                @media (max-width: 480px) {
                    section {
                        padding: 3rem 1rem !important;
                    }
                    .hero-grid h1 {
                        font-size: 1.75rem !important;
                        line-height: 1.2 !important;
                    }
                    h2 {
                        font-size: 1.55rem !important;
                    }
                    h3 {
                        font-size: 1.2rem !important;
                    }
                    /* Hero CTA buttons stack vertically */
                    .hero-grid div[style*="display: flex"][style*="gap: 1rem"] {
                        flex-direction: column !important;
                        width: 100% !important;
                    }
                    .hero-grid div[style*="display: flex"][style*="gap: 1rem"] a {
                        width: 100% !important;
                        text-align: center !important;
                        box-sizing: border-box !important;
                    }
                    .tab-switcher button {
                        font-size: 0.75rem !important;
                        padding: 0.45rem 0.7rem !important;
                    }

                    /* Hero graphics on mobile - elegant scaling approach */
                    .hero-graphics {
                        height: 310px !important;
                        position: relative !important;
                        overflow: visible !important;
                        width: 100% !important;
                        transform: scale(0.72) !important;
                        transform-origin: top center !important;
                        margin-bottom: 2rem !important;
                    }
                    .hero-graphics > div {
                        position: absolute !important;
                    }
                    /* Main profit card */
                    .hero-graphics > div:first-child {
                        width: calc(100% - 1rem) !important;
                        max-width: 380px !important;
                        right: 0 !important;
                        top: 0 !important;
                        padding: 1.5rem !important;
                        box-sizing: border-box !important;
                    }
                    /* RTO Prevention card */
                    .hero-graphics > div:nth-child(2) {
                        display: block !important;
                        bottom: 2% !important;
                        left: 2% !important;
                        top: auto !important;
                        max-width: 210px !important;
                        width: 210px !important;
                    }
                    /* Customer Retention card */
                    .hero-graphics > div:nth-child(3) {
                        display: block !important;
                        top: 38% !important;
                        left: 0% !important;
                        max-width: 195px !important;
                        width: 195px !important;
                    }
                }

                /* ===== 425px MOBILE FIXES ===== */
                @media (max-width: 425px) {
                    html, body {
                        overflow-x: hidden !important;
                    }
                    section {
                        padding: 2.5rem 1rem !important;
                    }
                    .hero-grid {
                        width: 100% !important;
                        box-sizing: border-box !important;
                        overflow: hidden !important;
                        padding: 0 !important;
                    }
                    .hero-grid h1 {
                        font-size: 1.65rem !important;
                        line-height: 1.2 !important;
                        white-space: normal !important;
                        overflow-wrap: break-word !important;
                        word-break: break-word !important;
                        padding: 0 0.5rem !important;
                        box-sizing: border-box !important;
                    }
                    .hero-grid p {
                        font-size: 0.9rem !important;
                        padding: 0 0.5rem !important;
                        box-sizing: border-box !important;
                        overflow-wrap: break-word !important;
                    }
                    .hero-grid div[style*="display: flex"][style*="gap: 1rem"] {
                        flex-direction: column !important;
                        width: 100% !important;
                        padding: 0 0.5rem !important;
                        box-sizing: border-box !important;
                    }
                    .hero-grid div[style*="display: flex"][style*="gap: 1rem"] a {
                        width: 100% !important;
                        text-align: center !important;
                        box-sizing: border-box !important;
                    }
                    #install {
                        padding: 1rem !important;
                        margin: 0 !important;
                        max-width: 100% !important;
                        box-sizing: border-box !important;
                    }
                    #install form {
                        flex-direction: column !important;
                        gap: 0.65rem !important;
                    }
                    #install input {
                        font-size: 0.82rem !important;
                        width: 100% !important;
                        box-sizing: border-box !important;
                    }
                    #install button {
                        padding: 0.75rem !important;
                        font-size: 0.85rem !important;
                        width: 100% !important;
                    }

                }

                /* ===== 320px FIXES ===== */
                @media (max-width: 380px) {
                    html, body {
                        overflow-x: hidden !important;
                    }

                    /* Header tighter */
                    .landing-header {
                        padding: 0.7rem 0.9rem !important;
                        min-height: 60px !important;
                    }
                    .logo-title {
                        font-size: 1.1rem !important;
                    }
                    .logo-subtitle {
                        font-size: 0.55rem !important;
                    }
                    .header-logo > div:first-child {
                        width: 32px !important;
                        height: 32px !important;
                        font-size: 1.1rem !important;
                    }

                    /* Section padding */
                    section {
                        padding: 2.5rem 0.9rem !important;
                    }

                    /* Hero */
                    .hero-grid {
                        gap: 1.75rem !important;
                        padding: 0 !important;
                    }
                    .hero-grid h1 {
                        font-size: 1.5rem !important;
                        line-height: 1.22 !important;
                        margin-bottom: 1rem !important;
                    }
                    .hero-grid p {
                        font-size: 0.92rem !important;
                        line-height: 1.55 !important;
                        margin-bottom: 1.5rem !important;
                    }

                    /* Hero badge pill */
                    .hero-grid div[style*="inline-flex"] {
                        font-size: 0.7rem !important;
                        padding: 0.35rem 0.75rem !important;
                        max-width: 100% !important;
                        overflow: hidden !important;
                        text-overflow: ellipsis !important;
                        white-space: nowrap !important;
                    }

                    /* Hero CTA buttons */
                    .hero-grid div[style*="display: flex"] a,
                    .hero-grid div[style*="display: flex"] button {
                        width: 100% !important;
                        text-align: center !important;
                        box-sizing: border-box !important;
                        font-size: 0.9rem !important;
                        padding: 0.75rem 1rem !important;
                    }

                    /* Install box */
                    #install {
                        padding: 1rem !important;
                        margin: 0 !important;
                        max-width: 100% !important;
                    }
                    #install input {
                        font-size: 0.82rem !important;
                        padding: 0.7rem 0.85rem !important;
                    }
                    #install button {
                        padding: 0.75rem !important;
                        font-size: 0.85rem !important;
                        width: 100% !important;
                    }

                    /* Hero graphics - scale even smaller for micro screens */
                    .hero-graphics {
                        height: 260px !important;
                        overflow: visible !important;
                        width: 100% !important;
                        transform: scale(0.58) !important;
                        transform-origin: top center !important;
                        margin-bottom: 2.5rem !important;
                    }

                    /* Headings */
                    h2 {
                        font-size: 1.4rem !important;
                        letter-spacing: -0.02em !important;
                    }
                    h3 {
                        font-size: 1.1rem !important;
                    }
                    h4 {
                        font-size: 1rem !important;
                    }

                    /* Feature/audience cards */
                    .glass-card {
                        padding: 1.25rem !important;
                    }

                    /* Tab switcher */
                    .tab-switcher {
                        padding-inline: 0.5rem !important;
                        gap: 0.3rem !important;
                    }
                    .tab-switcher button {
                        font-size: 0.72rem !important;
                        padding: 0.4rem 0.6rem !important;
                        border-radius: 8px !important;
                    }

                    /* Mockup container */
                    .glass-card[style*="minHeight: 400px"] {
                        padding: 1rem !important;
                        min-height: auto !important;
                    }

                    /* Cards compact padding */
                    .glass-card {
                        padding: 1.5rem 1.25rem !important;
                    }

                    /* Testimonials cards */
                    #testimonials div[style*="padding: 2.25rem"] {
                        padding: 1.5rem 1.25rem !important;
                    }

                    /* Stats numbers */
                    h4[style*="font-size: 1.6rem"],
                    h4[style*="font-size: 1.4rem"] {
                        font-size: 1.25rem !important;
                    }

                    /* Footer */
                    footer {
                        padding: 2.5rem 1rem 2rem !important;
                    }
                    footer div[style*="gap: 4rem"] {
                        gap: 2rem !important;
                        flex-direction: column !important;
                    }

                    /* Final CTA */
                    section[style*="linear-gradient(135deg, #0f172a"] h2 {
                        font-size: 1.5rem !important;
                    }
                    section[style*="linear-gradient(135deg, #0f172a"] p {
                        font-size: 0.92rem !important;
                    }
                    section[style*="linear-gradient(135deg, #0f172a"] div[style*="display: flex"] a {
                        width: 100% !important;
                        max-width: 260px !important;
                        padding: 0.75rem 1rem !important;
                        font-size: 0.9rem !important;
                    }

                    /* Trust bar items */
                    .trust-grid {
                        gap: 1.25rem !important;
                        flex-direction: column !important;
                        align-items: flex-start !important;
                    }

                    /* Mobile menu drawer */
                    .mobile-menu-drawer {
                        top: 60px !important;
                    }
                }

                /* ===== MOBILE: 375px ===== */
                @media (max-width: 375px) {
                    .logo-subtitle {
                        font-size: 0.52rem !important;
                    }
                }

                /* Scroll to top hover micro-interactions */
                .scroll-to-top-btn {
                    will-change: transform, opacity;
                }
                .scroll-to-top-btn:hover {
                    transform: scale(1.1) translateY(-2px) !important;
                    box-shadow: 0 12px 28px rgba(99, 102, 241, 0.45) !important;
                }
                .scroll-to-top-btn:active {
                    transform: scale(0.95) !important;
                }
            `}} />
        </div>

        {/* Scroll to Top Button - outside outer div so position:fixed isn't clipped */}
        <button
            className="scroll-to-top-btn"
            onClick={scrollToTop}
            style={{
                position: 'fixed',
                bottom: '2rem',
                right: '2rem',
                zIndex: 99999,
                background: 'linear-gradient(135deg, #6366f1 0%, #3b82f6 100%)',
                color: 'white',
                border: 'none',
                width: '50px',
                height: '50px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                boxShadow: '0 10px 25px rgba(99, 102, 241, 0.4)',
                outline: 'none',
                opacity: showScrollTop ? 1 : 0,
                pointerEvents: showScrollTop ? 'auto' : 'none',
                transform: showScrollTop ? 'scale(1) translateY(0)' : 'scale(0.8) translateY(20px)',
                transition: 'all 0.35s cubic-bezier(0.4, 0, 0.2, 1)'
            }}
            aria-label="Scroll to top"
        >
            <svg style={{ width: '22px', height: '22px' }} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 15l7-7 7 7" />
            </svg>
        </button>
        </>
    );
};
